import React, { useEffect, useMemo, useState } from "react";

import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  Chip,
  Stack,
  Divider,
  useMediaQuery,
  useTheme,
} from "@mui/material";

import SearchIcon from "@mui/icons-material/Search";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";

const COLORS = {
  black: "#111111",
  darkBlack: "#080808",
  orange: "#f97316",
  darkOrange: "#d35400",
  camel: "#b08968",
  gray: "#6b7280",
  tinGray: "#9ca3af",
  lightGray: "#f3f4f6",
  lighterGray: "#f8f8f8",
  border: "#e5e7eb",
  white: "#ffffff",
  green: "#15803d",
  greenLight: "#f0fdf4",
  red: "#dc2626",
  redLight: "#fef2f2",
  amber: "#b45309",
  amberLight: "#fffbeb",
  orangeLight: "#fff7ed",
};

const formatMoney = (value) => {
  const number = Number(value || 0);

  return number.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatDate = (value) => {
  if (!value) {
    return "N/A";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const getSemesterLabel = (semester) => {
  if (!semester) {
    return "N/A";
  }

  const normalized = String(semester).trim().toLowerCase();

  if (
    normalized === "first" ||
    normalized === "first semester" ||
    normalized === "semester one" ||
    normalized === "semester 1"
  ) {
    return "First Semester";
  }

  if (
    normalized === "second" ||
    normalized === "second semester" ||
    normalized === "semester two" ||
    normalized === "semester 2"
  ) {
    return "Second Semester";
  }

  return semester;
};

const getStudentTypeLabel = (studentType) => {
  const labels = {
    old: "Old Student",
    new: "New Student",
    scholarship: "Scholarship",
    ward: "Ward",
  };

  return labels[studentType] || studentType || "N/A";
};

const getBalanceColor = (balance) => {
  const value = Number(balance || 0);

  if (value <= 0) {
    return COLORS.green;
  }

  return COLORS.red;
};

const getBalanceBackground = (balance) => {
  const value = Number(balance || 0);

  if (value <= 0) {
    return COLORS.greenLight;
  }

  return COLORS.redLight;
};

const getPaymentCurrencyLabel = (payment) => {
  if (!payment?.payment_currency) {
    return "LRD";
  }

  return String(payment.payment_currency).toUpperCase();
};

const getPaymentAmountDisplay = (payment) => {
  const currency = getPaymentCurrencyLabel(payment);

  if (currency === "USD") {
    return `$${formatMoney(payment.amount_paid)}`;
  }

  return `L$${formatMoney(payment.amount_paid)}`;
};

const getAcademicYear = (payment) => {
  return (
    payment?.academic_year_name ||
    payment?.academic_year ||
    "N/A"
  );
};

const getStudentClass = (payment) => {
  return (
    payment?.student_class_name ||
    payment?.student_class ||
    "N/A"
  );
};

const getPaymentKey = (payment, index) => {
  return (
    payment?.id ||
    `${payment?.date || "payment"}-${index}`
  );
};

const UserDetailsPageTable = ({ payments = [] }) => {
  const theme = useTheme();

  const isMobile = useMediaQuery(
    theme.breakpoints.down("sm")
  );

  const isTablet = useMediaQuery(
    theme.breakpoints.down("md")
  );

  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const safePayments = Array.isArray(payments)
    ? payments
    : [];

  const filteredPayments = useMemo(() => {
    const normalizedSearch = search
      .trim()
      .toLowerCase();

    if (!normalizedSearch) {
      return safePayments;
    }

    return safePayments.filter((payment) => {
      const searchableValues = [
        payment?.semester,
        getSemesterLabel(payment?.semester),
        payment?.installment,
        getStudentClass(payment),
        payment?.student_type,
        getStudentTypeLabel(payment?.student_type),
        payment?.payment_currency,
        getPaymentCurrencyLabel(payment),
        payment?.payer_name,
        payment?.month,
        payment?.academic_year,
        payment?.academic_year_name,
        payment?.date,
        formatDate(payment?.date),
        payment?.amount_paid,
        payment?.amount_paid_lrd,
        payment?.total_fee,
        payment?.balance_fee,
      ];

      return searchableValues.some((value) =>
        String(value ?? "")
          .toLowerCase()
          .includes(normalizedSearch)
      );
    });
  }, [safePayments, search]);

  const totalPages = Math.max(
    1,
    Math.ceil(
      filteredPayments.length / rowsPerPage
    )
  );

  useEffect(() => {
    if (page >= totalPages) {
      setPage(totalPages - 1);
    }
  }, [page, totalPages]);

  const paginatedPayments = useMemo(() => {
    const startIndex = page * rowsPerPage;

    return filteredPayments.slice(
      startIndex,
      startIndex + rowsPerPage
    );
  }, [
    filteredPayments,
    page,
    rowsPerPage,
  ]);

  const handleSearchChange = (event) => {
    setSearch(event.target.value);
    setPage(0);
  };

  const handlePageChange = (_, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(
      parseInt(event.target.value, 10)
    );
    setPage(0);
  };

  if (safePayments.length === 0) {
    return (
      <Paper
        elevation={0}
        sx={{
          overflow: "hidden",
          borderRadius: 3,
          border: `1px solid ${COLORS.border}`,
          backgroundColor: COLORS.white,
        }}
      >
        <Box
          sx={{
            px: {
              xs: 2.5,
              sm: 3,
              md: 4,
            },
            py: {
              xs: 4,
              md: 5,
            },
            textAlign: "center",
          }}
        >
          <Box
            sx={{
              width: 58,
              height: 58,
              mx: "auto",
              mb: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "50%",
              backgroundColor: COLORS.orangeLight,
              color: COLORS.orange,
            }}
          >
            <ReceiptLongOutlinedIcon
              sx={{ fontSize: 29 }}
            />
          </Box>

          <Typography
            sx={{
              color: COLORS.black,
              fontSize: {
                xs: "1rem",
                md: "1.05rem",
              },
              fontWeight: 800,
              mb: 0.7,
            }}
          >
            No Payment Records
          </Typography>

          <Typography
            sx={{
              color: COLORS.gray,
              fontSize: "0.88rem",
              maxWidth: 430,
              mx: "auto",
              lineHeight: 1.6,
            }}
          >
            There are currently no payment
            transactions recorded for this
            student.
          </Typography>
        </Box>
      </Paper>
    );
  }

  return (
    <Box
      sx={{
        width: "100%",
      }}
    >
      {/* Section Header */}
      <Box
        sx={{
          mb: 2.5,
        }}
      >
        <Stack
          direction={{
            xs: "column",
            sm: "row",
          }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{
            xs: "flex-start",
            sm: "center",
          }}
        >
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
          >
            <Box
              sx={{
                width: 42,
                height: 42,
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: 2,
                backgroundColor:
                  COLORS.orangeLight,
                color: COLORS.orange,
              }}
            >
              <PaymentsOutlinedIcon
                sx={{ fontSize: 23 }}
              />
            </Box>

            <Box>
              <Typography
                sx={{
                  color: COLORS.black,
                  fontSize: {
                    xs: "1rem",
                    md: "1.1rem",
                  },
                  fontWeight: 800,
                  lineHeight: 1.3,
                }}
              >
                Payment History
              </Typography>

              <Typography
                sx={{
                  mt: 0.35,
                  color: COLORS.gray,
                  fontSize: {
                    xs: "0.76rem",
                    md: "0.82rem",
                  },
                  lineHeight: 1.5,
                }}
              >
                Complete record of payments
                made by or on behalf of this
                student.
              </Typography>
            </Box>
          </Stack>

          <Chip
            label={`${filteredPayments.length} ${
              filteredPayments.length === 1
                ? "transaction"
                : "transactions"
            }`}
            size="small"
            sx={{
              alignSelf: {
                xs: "flex-start",
                sm: "center",
              },
              backgroundColor:
                COLORS.lightGray,
              color: COLORS.black,
              border: `1px solid ${COLORS.border}`,
              fontWeight: 800,
              fontSize: "0.74rem",
            }}
          />
        </Stack>
      </Box>

      {/* Search and Summary Bar */}
      <Paper
        elevation={0}
        sx={{
          mb: 2,
          p: {
            xs: 1.5,
            sm: 2,
          },
          borderRadius: 2.5,
          border: `1px solid ${COLORS.border}`,
          backgroundColor: COLORS.lighterGray,
        }}
      >
        <Stack
          direction={{
            xs: "column",
            sm: "row",
          }}
          spacing={1.5}
          justifyContent="space-between"
          alignItems={{
            xs: "stretch",
            sm: "center",
          }}
        >
          <Box>
            <Typography
              sx={{
                color: COLORS.black,
                fontWeight: 700,
                fontSize: "0.82rem",
              }}
            >
              Transaction Records
            </Typography>

            <Typography
              sx={{
                color: COLORS.gray,
                fontSize: "0.74rem",
                mt: 0.25,
              }}
            >
              {search.trim()
                ? `Showing ${filteredPayments.length} matching records`
                : `Showing all ${safePayments.length} records`}
            </Typography>
          </Box>

          <TextField
            value={search}
            onChange={handleSearchChange}
            placeholder="Search payments..."
            size="small"
            fullWidth={isMobile}
            sx={{
              width: {
                xs: "100%",
                sm: 300,
                md: 330,
              },
              "& .MuiOutlinedInput-root": {
                minHeight: 40,
                backgroundColor:
                  COLORS.white,
                borderRadius: 2,
                fontSize: "0.82rem",

                "& fieldset": {
                  borderColor:
                    COLORS.border,
                },

                "&:hover fieldset": {
                  borderColor:
                    COLORS.orange,
                },

                "&.Mui-focused fieldset": {
                  borderColor:
                    COLORS.orange,
                  borderWidth: 1.5,
                },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon
                    sx={{
                      color: COLORS.orange,
                      fontSize: 20,
                    }}
                  />
                </InputAdornment>
              ),
            }}
          />
        </Stack>
      </Paper>

      {/* Payment Table */}
      <Paper
        elevation={0}
        sx={{
          overflow: "hidden",
          borderRadius: 3,
          border: `1px solid ${COLORS.border}`,
          backgroundColor: COLORS.white,
          boxShadow:
            "0 6px 22px rgba(0, 0, 0, 0.05)",
        }}
      >
        <TableContainer
          sx={{
            width: "100%",
            overflowX: "auto",
            "&::-webkit-scrollbar": {
              height: 8,
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor:
                COLORS.lightGray,
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor:
                COLORS.tinGray,
              borderRadius: 10,
            },
          }}
        >
          <Table
            size={isMobile ? "small" : "medium"}
            stickyHeader
            sx={{
              minWidth: isTablet
                ? 1450
                : 1500,

              "& .MuiTableCell-root": {
                verticalAlign: "middle",
              },
            }}
          >
            <TableHead>
              <TableRow>
                {[
                  "Semester",
                  "Installment",
                  "Academic Year",
                  "Class",
                  "Student Type",
                  "Currency",
                  "Amount Paid",
                  "Paid in LRD",
                  "Total Fee",
                  "Balance",
                  "Payer",
                  "Date",
                ].map((heading) => (
                  <TableCell
                    key={heading}
                    sx={{
                      backgroundColor:
                        COLORS.black,
                      color: COLORS.white,
                      fontWeight: 800,
                      fontSize: {
                        xs: "0.68rem",
                        md: "0.74rem",
                      },
                      letterSpacing:
                        "0.025em",
                      whiteSpace: "nowrap",
                      borderBottom: `3px solid ${COLORS.orange}`,
                      py: {
                        xs: 1.35,
                        md: 1.55,
                      },
                      px: {
                        xs: 1.4,
                        md: 1.8,
                      },
                    }}
                  >
                    {heading}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {paginatedPayments.length > 0 ? (
                paginatedPayments.map(
                  (payment, index) => {
                    const balance = Number(
                      payment?.balance_fee || 0
                    );

                    const currency =
                      getPaymentCurrencyLabel(
                        payment
                      );

                    const isPaid =
                      balance <= 0;

                    return (
                      <TableRow
                        key={getPaymentKey(
                          payment,
                          index
                        )}
                        hover
                        sx={{
                          backgroundColor:
                            index % 2 === 0
                              ? COLORS.white
                              : COLORS.lighterGray,

                          transition:
                            "background-color 0.15s ease",

                          "&:hover": {
                            backgroundColor:
                              COLORS.orangeLight,
                          },

                          "& td": {
                            borderBottom: `1px solid ${COLORS.border}`,
                          },

                          "&:last-child td": {
                            borderBottom: "none",
                          },
                        }}
                      >
                        {/* Semester */}
                        <TableCell
                          sx={{
                            px: {
                              xs: 1.4,
                              md: 1.8,
                            },
                            fontWeight: 700,
                            color:
                              COLORS.black,
                            whiteSpace:
                              "nowrap",
                            fontSize:
                              "0.78rem",
                          }}
                        >
                          {getSemesterLabel(
                            payment?.semester
                          )}
                        </TableCell>

                        {/* Installment */}
                        <TableCell
                          sx={{
                            px: {
                              xs: 1.4,
                              md: 1.8,
                            },
                          }}
                        >
                          <Chip
                            label={
                              payment?.installment ||
                              "N/A"
                            }
                            size="small"
                            sx={{
                              height: 26,
                              backgroundColor:
                                COLORS.orangeLight,
                              color:
                                COLORS.darkOrange,
                              border: `1px solid #fed7aa`,
                              fontWeight: 800,
                              fontSize:
                                "0.68rem",
                            }}
                          />
                        </TableCell>

                        {/* Academic Year */}
                        <TableCell
                          sx={{
                            px: {
                              xs: 1.4,
                              md: 1.8,
                            },
                            fontWeight: 700,
                            color:
                              COLORS.gray,
                            whiteSpace:
                              "nowrap",
                            fontSize:
                              "0.78rem",
                          }}
                        >
                          {getAcademicYear(
                            payment
                          )}
                        </TableCell>

                        {/* Class */}
                        <TableCell
                          sx={{
                            px: {
                              xs: 1.4,
                              md: 1.8,
                            },
                            fontWeight: 700,
                            color:
                              COLORS.black,
                            fontSize:
                              "0.78rem",
                          }}
                        >
                          {getStudentClass(
                            payment
                          )}
                        </TableCell>

                        {/* Student Type */}
                        <TableCell
                          sx={{
                            px: {
                              xs: 1.4,
                              md: 1.8,
                            },
                          }}
                        >
                          <Chip
                            label={getStudentTypeLabel(
                              payment?.student_type
                            )}
                            size="small"
                            sx={{
                              height: 26,
                              backgroundColor:
                                COLORS.lightGray,
                              color:
                                COLORS.black,
                              border: `1px solid ${COLORS.border}`,
                              fontWeight: 700,
                              fontSize:
                                "0.67rem",
                            }}
                          />
                        </TableCell>

                        {/* Currency */}
                        <TableCell
                          sx={{
                            px: {
                              xs: 1.4,
                              md: 1.8,
                            },
                          }}
                        >
                          <Chip
                            label={currency}
                            size="small"
                            sx={{
                              height: 26,
                              backgroundColor:
                                currency ===
                                "USD"
                                  ? COLORS.amberLight
                                  : COLORS.lightGray,
                              color:
                                currency ===
                                "USD"
                                  ? COLORS.amber
                                  : COLORS.black,
                              border: `1px solid ${
                                currency ===
                                "USD"
                                  ? "#fde68a"
                                  : COLORS.border
                              }`,
                              fontWeight: 900,
                              fontSize:
                                "0.68rem",
                            }}
                          />
                        </TableCell>

                        {/* Amount Paid */}
                        <TableCell
                          sx={{
                            px: {
                              xs: 1.4,
                              md: 1.8,
                            },
                            fontWeight: 900,
                            color:
                              COLORS.black,
                            whiteSpace:
                              "nowrap",
                            fontSize:
                              "0.82rem",
                          }}
                        >
                          {getPaymentAmountDisplay(
                            payment
                          )}
                        </TableCell>

                        {/* Paid in LRD */}
                        <TableCell
                          sx={{
                            px: {
                              xs: 1.4,
                              md: 1.8,
                            },
                            fontWeight: 800,
                            color:
                              COLORS.darkOrange,
                            whiteSpace:
                              "nowrap",
                            fontSize:
                              "0.8rem",
                          }}
                        >
                          L$
                          {formatMoney(
                            payment?.amount_paid_lrd
                          )}
                        </TableCell>

                        {/* Total Fee */}
                        <TableCell
                          sx={{
                            px: {
                              xs: 1.4,
                              md: 1.8,
                            },
                            fontWeight: 700,
                            color:
                              COLORS.gray,
                            whiteSpace:
                              "nowrap",
                            fontSize:
                              "0.8rem",
                          }}
                        >
                          L$
                          {formatMoney(
                            payment?.total_fee
                          )}
                        </TableCell>

                        {/* Balance */}
                        <TableCell
                          sx={{
                            px: {
                              xs: 1.4,
                              md: 1.8,
                            },
                          }}
                        >
                          <Chip
                            label={`L$${formatMoney(
                              balance
                            )}`}
                            size="small"
                            sx={{
                              height: 28,
                              backgroundColor:
                                getBalanceBackground(
                                  balance
                                ),
                              color:
                                getBalanceColor(
                                  balance
                                ),
                              border: `1px solid ${
                                isPaid
                                  ? "#bbf7d0"
                                  : "#fecaca"
                              }`,
                              fontWeight: 900,
                              fontSize:
                                "0.7rem",
                            }}
                          />
                        </TableCell>

                        {/* Payer */}
                        <TableCell
                          sx={{
                            px: {
                              xs: 1.4,
                              md: 1.8,
                            },
                            color:
                              COLORS.gray,
                            fontWeight: 600,
                            whiteSpace:
                              "nowrap",
                            fontSize:
                              "0.78rem",
                          }}
                        >
                          {payment?.payer_name ||
                            "N/A"}
                        </TableCell>

                        {/* Date */}
                        <TableCell
                          sx={{
                            px: {
                              xs: 1.4,
                              md: 1.8,
                            },
                            color:
                              COLORS.gray,
                            fontWeight: 600,
                            whiteSpace:
                              "nowrap",
                            fontSize:
                              "0.78rem",
                          }}
                        >
                          {formatDate(
                            payment?.date
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  }
                )
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={12}
                    align="center"
                    sx={{
                      py: 6,
                      px: 3,
                    }}
                  >
                    <Box
                      sx={{
                        maxWidth: 420,
                        mx: "auto",
                      }}
                    >
                      <Box
                        sx={{
                          width: 48,
                          height: 48,
                          mx: "auto",
                          mb: 1.5,
                          display: "flex",
                          alignItems:
                            "center",
                          justifyContent:
                            "center",
                          borderRadius:
                            "50%",
                          backgroundColor:
                            COLORS.orangeLight,
                          color:
                            COLORS.orange,
                        }}
                      >
                        <SearchIcon
                          sx={{
                            fontSize: 24,
                          }}
                        />
                      </Box>

                      <Typography
                        sx={{
                          color:
                            COLORS.black,
                          fontWeight: 800,
                          fontSize:
                            "0.95rem",
                          mb: 0.5,
                        }}
                      >
                        No matching
                        payments
                      </Typography>

                      <Typography
                        sx={{
                          color:
                            COLORS.gray,
                          fontSize:
                            "0.8rem",
                          lineHeight: 1.5,
                        }}
                      >
                        No payment records
                        match your current
                        search. Try another
                        name, class, semester,
                        currency, date, or
                        payment reference.
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Divider />

        {/* Pagination */}
        <TablePagination
          component="div"
          count={filteredPayments.length}
          page={page}
          onPageChange={handlePageChange}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={
            handleRowsPerPageChange
          }
          rowsPerPageOptions={[
            5,
            10,
            25,
            50,
          ]}
          labelRowsPerPage={
            isMobile
              ? "Rows"
              : "Rows per page"
          }
          sx={{
            backgroundColor:
              COLORS.lighterGray,

            "& .MuiTablePagination-toolbar":
              {
                minHeight: {
                  xs: 54,
                  sm: 58,
                },
                px: {
                  xs: 1,
                  sm: 2,
                },
              },

            "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows":
              {
                color: COLORS.gray,
                fontWeight: 600,
                fontSize: {
                  xs: "0.68rem",
                  sm: "0.74rem",
                },
              },

            "& .MuiTablePagination-select": {
              fontWeight: 700,
              fontSize: "0.78rem",
            },

            "& .MuiIconButton-root": {
              color: COLORS.black,
            },

            "& .MuiIconButton-root:hover": {
              color: COLORS.orange,
              backgroundColor:
                COLORS.orangeLight,
            },

            "& .MuiIconButton-root.Mui-disabled":
              {
                color: COLORS.tinGray,
              },
          }}
        />
      </Paper>
    </Box>
  );
};

export default UserDetailsPageTable;

